import Application from '../Application';
import Tool from './Tool';
import logging from '../logging';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Vector3 } from 'three';

const log = logging('tools');

/**
 * This orbit tool wraps the three.js OrbitControls.
 */
export default class OrbitTool extends Tool {

    target: Vector3 = new Vector3(12.75, 42, 0);

    private app: Application;
    controls: OrbitControls;

    constructor(app: Application) {
        super();

        this.app = app;

        this.controls = new OrbitControls(
            app.pipeline.camera, app.pipeline.canvas);

        // Always remain centered on the target by disabling panning
        this.controls.enablePan = false;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.25;
        this.controls.screenSpacePanning = false;
        this.controls.minDistance = 24;
        this.controls.maxDistance = 90;
        this.controls.minAzimuthAngle = -Math.PI/2;
        this.controls.maxAzimuthAngle = Math.PI/2;
        this.controls.target = this.target;
        this.controls.enabled = true;

        this.controls.update();

        this.update = this.update.bind(this);
        this.change = this.change.bind(this);
    }

    enable(): void {
        log.debug('OrbitTool.enable()');
        this.controls.enabled = true;
        this.controls.object = this.app.pipeline.camera;
        // remove a listener fist to avoid double registration
        this.app.pipeline.canvas.removeEventListener(
            'pointerdown', this.begin);
        this.app.pipeline.canvas.addEventListener('pointerdown', this.begin);

        this.app.pipeline.canvas.removeEventListener('wheel', this.change);
        this.app.pipeline.canvas.addEventListener('wheel', this.change);

        this.update();
    }

    disable(): void {
        log.debug('OrbitTool.disable()');
        this.controls.enabled = false;
        this.app.pipeline.canvas.removeEventListener(
            'pointerdown', this.begin);
        this.app.pipeline.canvas.removeEventListener('wheel', this.change);
    }

    // We expect to be activated by a pointerdown event
    begin(): void {
        log.debug('OrbitTool.begin()');
        // Every time we activate, we update the controls' reference to the
        // camera since the pipeline's camera object could change
        this.controls.object  = this.app.pipeline.camera;
        this.controls.addEventListener('change', this.change);
        this.app.pipeline.on('before-render', this.update);

        window.addEventListener('pointerup', this.end, {once: true});
    }

    cancel(): void {
        log.debug('OrbitTool.cancel()');
        this.controls.removeEventListener('change', this.change);
        this.app.pipeline.off('before-render', this.update);
    }

    end(): void {
        log.debug('OrbitTool.end()');
        this.cancel();
    }

    /**
     * Called before render to update the controls
     */
    private update(): void {
        //log.debug('OrbitTool.update()');
        if(this.controls.enabled) this.controls.update();
    }

    /**
     * Called by the orbit controls whenever an update results in a change in
     * the camera position.
     */
    private change(): void {
        //log.debug('OrbitTool.change()');
        this.app.pipeline.update = true;
    }
}
